import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import get from 'lodash/get';
import map from 'lodash/map';
import _unescape from 'lodash/unescape';
import ErrorIcon from '@material-ui/icons/Error';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import clsx from 'clsx';

import { FieldItemList } from 'models';
import { formatListData } from 'utils';
import safeParseHtml from 'shared/utils/safeParseHtml';

import { useStyles } from './style';

interface Props<T = {}> {
  list: FieldItemList<T>[];
  data: T;
  notificationCode?: number;
  sharesMovesDate?: string | null;
}

function getTooltipData(sharesMovesDate?: string | null) {
  const t1 =
    'Taloyhtiön osakeluettelon ylläpito on Maanmittauslaitoksen huoneistotietojärjestelmässä ja isännöintijärjestelmän tietoihin kirjataan merkintäpäivä Maanmittauslaitoksen rekisteröinti-ilmoituksen mukaan. <br /> RS-kohteissa ennen valmistumista tehtyihin kauppoihin RS-pankki hakee huoneistojen omistusoikeuden rekisteröinnit keskitetysti Maanmittauslaitokselta. Autopaikkojen ja muiden RS menettelyyn kuulumattomien tilojen osalta osakkaan tulee hakea rekisteröintiä itse, samoin kuin valmistumisen jälkeen tehdyissä kaupoissa myös huoneistojen osalta ostajan tulee hakea omistusoikeuden rekisteröintiä itse Maanmittauslaitokselta.';
  const l1 =
    'https://www.maanmittauslaitos.fi/huoneistot-ja-kiinteistot/huoneistot-ja-asuntokauppa/asiointiapuri';
  const lt1 =
    'https://www.maanmittauslaitos.fi/huoneistot-ja-kiinteistot/huoneistot-ja-asuntokauppa/asiointiapuri';
  const t2 =
    'Voidaksemme tehdä tilan omistusta koskevan osakeluettelomerkinnän, pyydämme Teitä toimittamaan todistuksen varainsiirtoverosta (kuitteja ei tarvitse toimittaa) sähköpostin liitteenä asiakaspalveluun. Yhteystiedot löytyvät Taloyhtiön palvelutoimittajat -osiosta. (aihekenttään teksti ”osakeluettelomerkintä - taloyhtiön nimi”). <br /> Katso verohallinnon ohje.';
  const l2 = 'https://vero.fi/henkiloasiakkaat/asuminen';
  const lt2 = 'vero.fi/henkiloasiakkaat/asuminen';

  return {
    text: sharesMovesDate ? t1 : t2,
    linkText: sharesMovesDate ? lt1 : lt2,
    link: sharesMovesDate ? l1 : l2,
  };
}

function FieldsList<T extends {}>({
  data,
  list,
  notificationCode,
  sharesMovesDate,
}: React.PropsWithChildren<Props<T>>) {
  const classes = useStyles();
  const { t } = useTranslation();

  const [openTooltip, setOpenTooltip] = useState(false);

  const handleOpenTooltip = () => {
    setOpenTooltip(true);
  };

  const handleCloseTooltip = () => {
    setOpenTooltip(false);
  };

  const renderList = (field: FieldItemList<T>) => {
    return map(get(data, field.id), field.render);
  };

  const renderTooltip = () => {
    const { link, linkText, text } = getTooltipData(sharesMovesDate);

    return (
      <div className={classes.tooltipWrapper}>
        <span className={classes.tooltipText}>{safeParseHtml(text)}</span>
        <a
          className={classes.tooltipLink}
          href={link}
          target="_blank"
          rel="noopener noreferrer"
        >
          {linkText}
        </a>
      </div>
    );
  };

  const renderGroupTitle = (text: string, idx: number) => (
    <li key={idx} className={classes.groupTitleWrapper}>
      <span className={classes.groupTitle}>{text}</span>
    </li>
  );

  return (
    <ul className={classes.list}>
      {list.map((field, idx) => {
        const { showTitle = true, align } = field;

        const isWarning =
          notificationCode &&
          notificationCode === get(field, 'notificationCode');

        if (field.show && !field.show(data)) return null;

        return (
          <>
            {field.header && field.header.position === 'before'
              ? renderGroupTitle(field.header.text, idx)
              : null}
            {field.format === 'list' ? (
              renderList(field)
            ) : field.renderFullItem ? (
              field.renderFullItem(data)
            ) : (
              <li key={field.id} className={classes.li}>
                {isWarning ? (
                  <ClickAwayListener onClickAway={handleCloseTooltip}>
                    <Tooltip
                      open={openTooltip}
                      title={renderTooltip()}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                      classes={{
                        tooltip: classes.tooltip,
                        popper: classes.popper,
                      }}
                    >
                      <Button
                        className={classes.btn}
                        onClick={handleOpenTooltip}
                      >
                        <ErrorIcon className={classes.icon} />
                      </Button>
                    </Tooltip>
                  </ClickAwayListener>
                ) : null}
                <div className={classes.item}>
                  {field.key && showTitle ? (
                    <span className={classes.title}>{t(field.key)}</span>
                  ) : null}
                  {field.format === 'html' ? (
                    <span
                      className={clsx(classes.html, {
                        [classes.fullWidth]: !showTitle,
                        [classes.alignLeft]: align === 'left',
                        [classes.alignCenter]: align === 'center',
                        [classes.alignRight]: align === 'right',
                      })}
                    >
                      {typeof get(data, field.id) === 'string' &&
                        safeParseHtml(_unescape(get(data, field.id)))}
                    </span>
                  ) : (
                    <span className={classes.value}>
                      {field.render
                        ? field.render(data)
                        : formatListData(get(data, field.id), field)}
                    </span>
                  )}
                </div>
              </li>
            )}
            {field.header && field.header.position === 'after'
              ? renderGroupTitle(field.header.text, idx)
              : null}
          </>
        );
      })}
    </ul>
  );
}

export default memo(FieldsList);
